import { createContext, useContext, useState, useEffect, Suspense } from "react";
import { useLocation, Outlet } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

import theme from "assets/theme-dashboard";
import themeDark from "assets/theme-dark";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import sidenavRoutes from "sidenavRoutes";
import { filterRoutesByRole } from "routes";

import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { useAuth } from "@clerk/clerk-react";
import { Triangle } from "react-loader-spinner";
import { motion } from "framer-motion";

const API_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

const AuthLayoutContext = createContext(null);

export function useAuthLayout() {
  const ctx = useContext(AuthLayoutContext);
  if (!ctx) throw new Error("useAuthLayout must be used within AuthenticatedLayout");
  return ctx;
}

function ContentFallback() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "60vh",
      }}
    >
      <Triangle color="#344767" ariaLabel="triangle-loading" visible={true} />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 1 }}
        style={{ marginTop: 12, fontSize: 18, color: "#555", fontFamily: "sans-serif" }}
      >
        Lade Modul...
      </motion.div>
    </div>
  );
}

function AuthenticatedLayout() {
  const [controller, dispatch] = useMaterialUIController();
  const { pathname } = useLocation();
  const {
    miniSidenav,
    direction,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);

  const { userId } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);

  // Fetch user roles
  useEffect(() => {
    if (!userId || userId === "undefined") return;
    setIsLoading(true);
    const abortController = new AbortController();
    const t = setTimeout(() => abortController.abort(), 10000);
    fetch(`${API_URL}/users/fetchUserRoles/${userId}`, {
      headers: { "x-api-key": API_KEY },
      signal: abortController.signal,
    })
      .then((response) =>
        response.ok ? response.json() : Promise.reject(new Error(`Roles ${response.status}`))
      )
      .then((data) => {
        setUserRoles(Array.from(data));
      })
      .catch((error) => {
        console.error("Error fetching roles:", error);
      })
      .finally(() => {
        clearTimeout(t);
        setIsLoading(false);
      });
    return () => {
      abortController.abort();
      clearTimeout(t);
    };
  }, [userId]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const filteredRoutes = filterRoutesByRole(sidenavRoutes, userRoles);

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Triangle color="#344767" ariaLabel="triangle-loading" visible={true} />
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1 }}
          style={{
            marginTop: 12,
            fontSize: 18,
            color: "#555",
            fontFamily: "sans-serif",
          }}
        >
          Lade Daten...
        </motion.div>
      </div>
    );
  }

  return (
    <AuthLayoutContext.Provider value={{ userRoles, userId, isLoading }}>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <DashboardLayout>
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandName="ElecTrack"
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
          <DashboardNavbar />
          <Suspense fallback={<ContentFallback />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </ThemeProvider>
    </AuthLayoutContext.Provider>
  );
}

export default AuthenticatedLayout;
